/**
 * PublicApiCatalog — R3-S3 (F3.5)
 *
 * Rendert den v1-Endpunkt-Katalog der stabilen Public API in genau den
 * Kategorien und der Reihenfolge des Monolithen (app/views/home/api.haml).
 * Datenquelle: features/developer/v1Endpoints.ts.
 */
import { useCallback, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Accordion, AccordionItem } from '../../components/Accordion'
import { METHOD_COLORS } from './types'
import type { HttpMethod } from './types'
import { V1_ENDPOINT_CATALOG, V1_ENDPOINT_COUNT, buildV1Curl } from '../developer/v1Endpoints'
import type { V1Endpoint } from '../developer/v1Endpoints'

function MethodBadge({ method }: { method: string }) {
  const colors = METHOD_COLORS[method.toUpperCase() as HttpMethod] ?? METHOD_COLORS.GET
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'monospace',
        backgroundColor: colors.background,
        color: colors.text,
        letterSpacing: '0.05em',
      }}
    >
      {method}
    </span>
  )
}

function CatalogRow({ endpoint }: { endpoint: V1Endpoint }) {
  const { t } = useTranslation('apidocs')
  const [copied, setCopied] = useState(false)
  const curl = buildV1Curl(endpoint)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(curl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard-API nicht verfügbar
    }
  }, [curl])

  const label = t(`publicApi.endpoints.${endpoint.labelKey}`)
  const methodLabel = endpoint.methodAlias
    ? `${endpoint.method}/${endpoint.methodAlias}`
    : endpoint.method

  const title = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <MethodBadge method={methodLabel} />
      <code style={{ fontSize: 13, color: 'var(--text)', fontFamily: 'monospace' }}>{endpoint.path}</code>
      <span style={{ color: 'var(--text-subtle)', fontSize: 13 }}>{label}</span>
    </div>
  )

  return (
    <AccordionItem id={`v1-${endpoint.method}-${endpoint.path}`} title={title}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{t('endpoint.curlExample')}</h4>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => { void handleCopy() }}
          aria-label={t('endpoint.copyAriaLabel')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
          {copied ? t('endpoint.copied') : t('endpoint.copy')}
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: 12,
          backgroundColor: 'var(--bg-elev)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          fontSize: 11,
          fontFamily: 'monospace',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {curl}
      </pre>
    </AccordionItem>
  )
}

export function PublicApiCatalog() {
  const { t } = useTranslation('apidocs')

  return (
    <section data-testid="public-api-catalog" aria-label={t('publicApi.title')} style={{ marginBottom: 32 }}>
      <h2
        style={{
          margin: '0 0 6px',
          fontSize: 18,
          fontWeight: 700,
          borderBottom: '1px solid var(--border)',
          paddingBottom: 8,
        }}
      >
        {t('publicApi.title')}
      </h2>
      <p style={{ margin: '8px 0 4px', fontSize: 13, color: 'var(--text-subtle)' }}>
        {t('publicApi.intro')}
      </p>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text-subtle)' }}>
        {t('publicApi.count', { total: V1_ENDPOINT_COUNT })}
      </p>

      {V1_ENDPOINT_CATALOG.map((category) => (
        <div key={category.id} data-testid={`public-api-group-${category.id}`} style={{ marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>
            {t(`publicApi.categories.${category.labelKey}`)}
          </h3>
          <Accordion>
            {category.endpoints.map((endpoint) => (
              <CatalogRow key={`${endpoint.method}-${endpoint.path}`} endpoint={endpoint} />
            ))}
          </Accordion>
        </div>
      ))}
    </section>
  )
}
